import * as React from 'react';
import {PropsWithChildren, useCallback, useContext} from 'react';
import DataService from '../../shared/services/DataService';
import UserService from '../../shared/services/UserService';
import {useCache} from '../hooks/useCache';
import {ReportCache} from '../context/ReportCache';
import {Report} from '../../reports/types/Report';
import {ProductLineFilter} from '../../shell/context/ProductLineFilter';
import CodeHelpers from '../helpers/CodeHelpers';
import {useLoadedData} from '../hooks/useLoadedData';
import {ReportListEntry} from '../../reports/types/ReportListEntry';
import {isNil} from 'lodash-es';

export const ReportStore = (props: PropsWithChildren<unknown>) => {
  const {currentProductLine} = useContext(ProductLineFilter);

  const {
    getValue: getDraftReport,
    clearCache: clearDraftReportCache
  } = useCache<string, Report>(async (reportId) => {
      return await DataService.tryLoad<Report>(
        '/engage-reports/load-draft-report',
        {
          organisationId: UserService.instance.organisationId,
          reportId: reportId
        }
      );
    }
  );

  const {
    getValue: getPublishedReport,
    clearCache: clearPublishedReportCache
  } = useCache<string, Report>(async (reportId) => {
      return await DataService.tryLoad<Report>(
        '/engage-reports/load-published-report',
        {
          organisationId: UserService.instance.organisationId,
          reportId: reportId
        }
      );
    }
  );

  const [availableReports, isLoadingAvailableReports, reloadAvailableReports] = useLoadedData<ReportListEntry[]>(
    '/engage-reports/load-reports',
    {
      organisationId: UserService.instance.organisationId,
      productLineCode: CodeHelpers.codeForProductLine(currentProductLine),
      showOnlyDirectReports: true
    },
    {
      hideErrorMessages: true
    }
  );

  const clearReportCache = useCallback(
    (reportId?: string) => {
      if (isNil(reportId)) {
        reloadAvailableReports();
      }

      clearDraftReportCache(reportId);
      clearPublishedReportCache(reportId);
    },
    [
      reloadAvailableReports,
      clearDraftReportCache,
      clearPublishedReportCache
    ]
  );

  const getAvailableReports = useCallback(
    () => {
      if (isLoadingAvailableReports) {
        return [];
      }

      return availableReports;
    },
    [
      availableReports
    ]
  );

  return (
    <ReportCache.Provider
      value={{
        getAvailableReports: getAvailableReports,
        getDraftReport: getDraftReport,
        getPublishedReport: getPublishedReport,
        clearReportCache: clearReportCache
      }}
    >
      {props.children}
    </ReportCache.Provider>
  );
};
